import { useModalStatusMessage } from '@/hooks';
import { useFormatters } from '@/hooks/useFormatters';
import { useGlobalModal } from '@/hooks/useGlobalModal';
import { getTicketIdWithHash } from '@/utils/getTicketIdWithHash';
import { useMutation, useSuspenseQuery } from '@apollo/client';
import { DateTimePickerController } from '@components/controllers/DateTimePickerController';
import { TextFieldController } from '@components/controllers/TextFieldController';
import DeviceNameSpan from '@components/deviceNameSpan';
import GlobalModal from '@components/GlobalModal';
import ModalActions from '@components/modal/ModalActions';
import ModalTitle from '@components/modal/ModalTitle';
import UPDATE_DGA_TICKET from '@features/dga/graphql/mutations/updateTicket';
import GET_DGA_TICKET_STATE from '@features/dga/graphql/queries/getDGATicketState';
import StandardAutocompleteIdsWithFallback from '@features/standardDesign/components/autocomplete/StandardAutocompleteIds';
import { zodResolver } from '@hookform/resolvers/zod';
import { type I18n } from '@lingui/core';
import { msg, t, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { Box, FormHelperText, Link, Skeleton, Typography } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers-latest';
import { AdapterDayjs } from '@mui/x-date-pickers-latest/AdapterDayjs';
import dayjs from 'dayjs';
import { FC, Suspense, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod/v4';
import { TicketDisplayProps } from './TicketDisplay';

interface Props {
  selectableDeviceIds?: string[];
  initialDeviceId?: string;
  onClose: () => void;
}

const ValidationSchema = (i18n: I18n) =>
  z.object({
    deviceId: z.string(),
    ticketId: z.string().min(1, t(i18n)`Este campo no puede estar vacío`),
    date: z.custom<dayjs.Dayjs>((val) => val instanceof dayjs, t(i18n)`Fecha inválida`),
  });

type FormValues = z.output<ReturnType<typeof ValidationSchema>>;

const getDefaultValues = (deviceId: string) => ({
  deviceId,
  ticketId: '',
  date: dayjs(),
});

interface OpenTicketFormProps {
  deviceId: string;
  updateTicket: (input: { deviceId: string; ticketId: string; date: Date }) => Promise<void>;
  onClose: () => void;
}

const OpenTicketForm: FC<OpenTicketFormProps> = ({ deviceId, updateTicket, onClose }) => {
  const { _, i18n } = useLingui();

  const { handleSubmit, control } = useForm<FormValues>({
    defaultValues: getDefaultValues(deviceId),
    resolver: zodResolver(ValidationSchema(i18n)),
  });

  const onSubmit = handleSubmit(async (data) => {
    await updateTicket({
      deviceId: data.deviceId,
      ticketId: data.ticketId,
      date: data.date.toDate(),
    });
  });

  return (
    <form onSubmit={onSubmit}>
      <Box px={2} mb={2}>
        <TextFieldController
          control={control}
          name="ticketId"
          label={_(msg`Id de ticket`)}
          slotProps={{
            textField: {
              fullWidth: true,
            },
          }}
        />

        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DateTimePickerController
            control={control}
            name="date"
            label={_(msg`Fecha de ingreso en la DGA`)}
            sx={{ mt: 2 }}
            slotProps={{
              textField: {
                fullWidth: true,
              },
            }}
          />
        </LocalizationProvider>
        <FormHelperText>
          Si aún no has creado el ticket en la DGA, puedes crearlo desde{' '}
          <Link href="https://snia.mop.gob.cl/ticket/mee">este enlace.</Link>
        </FormHelperText>
      </Box>
      <ModalActions
        onSubmit="submit"
        onClose={onClose}
        submitLabel={_(msg`Abrir ticket`)}
        closeButton={{
          label: _(msg`Cancelar`),
        }}
      />
    </form>
  );
};

interface CloseTicketForm {
  deviceId: string;
  onClose: () => void;
  closeTicket: () => void;
  ticket: TicketDisplayProps['ticket'] & { openedAt: number };
}

const CloseTicketForm: FC<CloseTicketForm> = ({ deviceId, closeTicket, onClose, ticket }) => {
  const { _ } = useLingui();
  const { formatDateTime } = useFormatters();

  return (
    <>
      <Box px={2} mb={2} display="grid" gap={2}>
        <Typography display="block">
          <Trans>
            El dispositivo{' '}
            <b>
              <DeviceNameSpan deviceId={deviceId} />
            </b>{' '}
            tiene un ticket abierto.
          </Trans>
        </Typography>

        {/* <TicketDisplay ticket={ticket} sx={{ maxWidth: 330, width: '100%' }} /> */}
        <Box display="grid" gridTemplateColumns="auto 1fr" columnGap={4} px={2}>
          <Typography textAlign="right">Fecha de creación</Typography>
          <Typography fontWeight="bold">{formatDateTime(ticket.openedAt)}</Typography>
          <Typography textAlign="right">ID de ticket</Typography>
          <Typography fontWeight="bold">{getTicketIdWithHash(ticket.id)}</Typography>
          <Typography textAlign="right">Dispositivo</Typography>
          <Typography fontWeight="bold">
            <DeviceNameSpan deviceId={deviceId} />
          </Typography>
        </Box>
      </Box>
      <ModalActions
        onSubmit={closeTicket}
        onClose={onClose}
        closeButton={{
          label: _(msg`Cancelar`),
        }}
        submitLabel={_(msg`Cerrar ticket`)}
        submitColor="error"
      />
    </>
  );
};

const HasDgaTicketsWrapper: FC<{
  deviceId: string;
  children: (
    ticket:
      | (TicketDisplayProps['ticket'] & {
          openedAt: number;
        })
      | null,
  ) => JSX.Element;
}> = ({ deviceId, children }) => {
  const { data } = useSuspenseQuery(GET_DGA_TICKET_STATE, {
    variables: {
      deviceId,
    },
  });
  const ticket = data?.device.dga?.ticket ?? null;
  return children(
    ticket
      ? {
          deviceId,
          openedAt: ticket.timestamp,
          id: ticket.id,
        }
      : null,
  );
};

const Content: FC<Props> = (props) => {
  const { _ } = useLingui();
  const [deviceId, setDeviceId] = useState<string | null>(props.initialDeviceId ?? null);
  const deviceIds = props.selectableDeviceIds;
  const { openModalErrorMessage, openModalSuccessMessage } = useModalStatusMessage();
  const [updateTicket] = useMutation(UPDATE_DGA_TICKET);

  return (
    <>
      <ModalTitle onClose={props.onClose} title={<Trans>Configurar ticket DGA</Trans>} />
      <Box px={2} mt={1}>
        {deviceIds?.length && (
          <StandardAutocompleteIdsWithFallback
            dataType="devices"
            value={deviceId}
            onChange={(_, value) => setDeviceId(value)}
            sx={{ mb: 2 }}
            queryOptions={{
              devices: {
                ids: props.selectableDeviceIds,
              },
            }}
          />
        )}
      </Box>
      {deviceId ? (
        <Suspense fallback={<Skeleton width="100%" height={300} variant="rounded" />}>
          <HasDgaTicketsWrapper deviceId={deviceId}>
            {(ticket) =>
              ticket ? (
                <CloseTicketForm
                  deviceId={deviceId}
                  onClose={props.onClose}
                  ticket={ticket}
                  closeTicket={async () => {
                    updateTicket({
                      variables: {
                        deviceId,
                        ticketId: ticket.id,
                        ticketTimestamp: ticket.openedAt,
                        status: 'closed',
                      },
                      onCompleted() {
                        openModalSuccessMessage(_(msg`Se ha cerrado el ticket correctamente.`));
                        props.onClose();
                      },
                      onError() {
                        openModalErrorMessage(_(msg`Error al cerrar ticket`));
                      },
                    });
                  }}
                />
              ) : (
                <OpenTicketForm
                  onClose={props.onClose}
                  deviceId={deviceId}
                  updateTicket={async (data) => {
                    updateTicket({
                      variables: {
                        deviceId: data.deviceId,
                        ticketId: data.ticketId,
                        ticketTimestamp: data.date.getTime(),
                        status: 'open',
                      },
                      onCompleted() {
                        openModalSuccessMessage(_(msg`Se ha abierto un nuevo ticket.`));
                        props.onClose();
                      },
                      onError() {
                        openModalErrorMessage(_(msg`Error al abrir ticket`));
                      },
                    });
                  }}
                />
              )
            }
          </HasDgaTicketsWrapper>
        </Suspense>
      ) : (
        <ModalActions onClose={props.onClose} />
      )}
    </>
  );
};

export const DgaTicketModal: FC = () => {
  const { globalModalProps, state, onClose } = useGlobalModal('dgaTicketModal');

  return (
    <GlobalModal {...globalModalProps} dialogProps={{ maxWidth: 'sm' }}>
      {state.open && (
        <>
          <Content
            selectableDeviceIds={
              'selectableDeviceIds' in state ? state.selectableDeviceIds : undefined
            }
            initialDeviceId={state.initialDeviceId}
            onClose={onClose}
          />
        </>
      )}
    </GlobalModal>
  );
};
